import React from "react";
import styled, { css } from "styled-components";

import * as Color from "~/Color";
import { Spacing } from "~/Layout";

type Props = {
  readonly jumbo?: boolean;
  readonly large?: boolean;
  readonly small?: boolean;
  readonly data?: boolean;
  readonly thin?: boolean;
  readonly strong?: boolean;
  readonly muted?: boolean;
  readonly color?: Color.Color;
  readonly align?: "left" | "center" | "right";
  readonly style?: React.CSSProperties;
};

export const color = (): Color.Color => Color.black();

export const Component: React.Fn<Props> = ({ children, ...props }) => (
  <Container.Component {...props}>{children}</Container.Component>
);

namespace Container {
  export const Component = styled.span<Props>`
    display: inline-block;

    ${({ strong, thin }) =>
      strong ? "font-weight: 600;" : thin ? "font-weight: 300;" : ""}

    ${({ jumbo }) => (jumbo ? `font-size: ${H1.fontSize()};` : "")}
    ${({ large }) => (large ? "font-size: 1.1em;" : "")}
    ${({ small }) => (small ? "font-size: 0.9em; line-height: 1.1em;" : "")}
    ${({ data }) => (data ? `font-size: ${FontSize.data()};` : "")}
    ${({ muted }) => (muted ? `color: ${Muted.color()};` : "")}
    ${({ color }) => (color ? `color: ${color};` : "")}
    ${({ align }) => (align ? `text-align: ${align};` : "")}
  `;
}

export namespace Muted {
  export const color = (): Color.Color => Color.steel();
}

type Header = Spacing.Props;

const margin = () => ({
  top: "0.7em",
  bottom: "0.2em",
});

const shared = () => css`
  color: ${Color.black()};
  line-height: normal;
  font-weight: 500;
`;

export namespace H1 {
  export const fontSize = () => "1.5em" as const;
  export const Component = styled.h1<Header>`
    font-size: ${() => fontSize()};
    ${() => shared()}
    ${(props) => Spacing.styles(Spacing.margin())(margin())(props)}
  `;
}

export namespace H2 {
  export const fontSize = () => "1.25em" as const;
  export const Component = styled.h2<Header>`
    font-size: ${() => fontSize()};
    ${() => shared()}
    ${(props) => Spacing.styles(Spacing.margin())(margin())(props)}
  `;
}

export namespace H3 {
  export const fontSize = () => "1.2em" as const;
  export const Component = styled.h3<Header>`
    font-size: ${() => fontSize()};
    ${() => shared()}
    ${(props) => Spacing.styles(Spacing.margin())(margin())(props)}
  `;
}

export namespace H4 {
  export const fontSize = () => "1em" as const;
  export const Component = styled.h4<Header>`
    font-size: ${() => fontSize()};
    ${() => shared()}
    ${(props) => Spacing.styles(Spacing.margin())(margin())(props)}
  `;
}

export namespace FontSize {
  export const base = () => "16px" as const;
  export const data = () => "14px" as const;

  export namespace D3 {
    export const base = () => "16" as const;
    export const data = () => "14" as const;
  }
}
